import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { CssBaseline, ThemeProvider } from "@mui/material";
import theme from "./theme";
import { authService } from "./utils/authService";
import { LandingPage } from "./views/landingPage/landingPage";
import { UserManagement } from "./views/users/userManagement";
import UserList from "./views/users/userList";
import ScheduleCalendar from "./views/schedules/scheduleCalendar";
import ServicesDashboard from "./views/dashboard/dashboard";
import ChecklistQuestionManagement from "./views/questions/questionsManagement";
import ChecklistQuestionList from "./views/questions/questionsList";
import { ClientManagement } from "./views/clients/clientsManagement";
import Login from "./views/login/Login";
import ClientList from "./views/clients/clientsList";
import Header from "./views/header/Header";
import InventoryList from "./views/inventory/inventoryList";
import StatusDashboard from "./views/dashboard/statusDashboard";
import ServiceDetail from "./views/landingPage/components/interest/ServiceDetail";
import ServiceOfferingsManager from "./views/serviceOfferingsManager/serviceOfferingsManager";
import QuotationCard from "./views/quotationCard/QuotationCard";
import ReportsList from "./views/reports/reportsList";
import "./i18n";


function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const handleLogin = (token) => {
    authService.setToken(token);
    setIsAuthenticated(true);
    navigate("/schedule");
  };

  const handleLogout = () => {
    authService.removeToken();
    authService.clearUserData();
    setIsAuthenticated(false);
    navigate("/login");
  };

  useEffect(() => {
    const checkAuthStatus = () => {
      const token = authService.getToken();
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split(".")[1]));
          const isTokenExpired = Date.now() >= payload.exp * 1000;
          if (isTokenExpired) {
            handleLogout();
          } else {
            setIsAuthenticated(true);
          }
        } catch (error) {
          handleLogout();
        }
      } else {
        setIsAuthenticated(false);
      }
      setIsLoading(false);
    };

    checkAuthStatus();
  });

  const ProtectedRoute = ({ children, allowedRoles = ["user", "admin"] }) => {
    const user = authService.getUserData();
    const hasPermission = allowedRoles.includes(user?.role);

    if (isLoading) {
      return null;
    }

    if (!isAuthenticated) {
      return <Navigate to="/login" replace />;
    }

    if (!hasPermission) {
      return <Navigate to="/schedule" replace />;
    }

    return children;
  };

  if (isLoading) {
    return null;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Header isAuthenticated={isAuthenticated} onLogout={handleLogout} />
      <Routes>
        <Route
          path="/login"
          element={
            !isAuthenticated ? (
              <Login onLogin={handleLogin} />
            ) : (
              <Navigate to="/schedule" />
            )
          }
        />
        <Route
          path="/landing"
          element={
            !isAuthenticated ? <LandingPage /> : <Navigate to="/schedule" />
          }
        />
        <Route
          path="/schedule"
          element={
            <ProtectedRoute>
              <ScheduleCalendar />
            </ProtectedRoute>
          }
        />
         <Route
          path="/cotizaciones"
          element={
            <ProtectedRoute>
              <QuotationCard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-offerings"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <ServiceOfferingsManager />
            </ProtectedRoute>
          }
        />

        <Route path="/services/:id" element={<ServiceDetail />} />
        <Route
          path="/reports"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <ReportsList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/status"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <StatusDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/services"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <ServicesDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/user-management"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <UserManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/user-management/:id"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <UserManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/questions-management"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <ChecklistQuestionManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/questions-list"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <ChecklistQuestionList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/inventory-list"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <InventoryList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client-management"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <ClientManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/user-list"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <UserList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client-list"
          element={
            <ProtectedRoute allowedRoles={["admin", "user"]}>
              <ClientList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/"
          element={<Navigate to={isAuthenticated ? "/schedule" : "/landing"} />}
        />
        <Route
          path="*"
          element={<Navigate to={isAuthenticated ? "/schedule" : "/landing"} />}
        />
      </Routes>
    </ThemeProvider>
  );
}

export default App;
